/**
 * Trust seal strip — premium pill row with icons (SSL, payments, garantie, …).
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, clipOutroFade, combineSceneFragments, escapeHtml, makeTl,
  typeSize, weights, withAlpha, colors as themeColors, gsapSetLine,
} from "../../reel/sceneShared.js";
import { lucideIcon, splitItem, sceneHeader } from "./_mockHelpers.js";

const ICON_ALIASES: Record<string, string> = {
  shieldcheck: "shield", shield: "shield",
  badgecheck: "badge", badge: "badge",
  creditcard: "credit", card: "credit", stripe: "credit",
  flag: "flag", made: "flag",
  headphones: "headphones", support: "headphones",
  lock: "lock", ssl: "lock",
  bolt: "bolt", zap: "zap",
  check: "check", star: "star",
};

export const trustSealStrip: SceneBuilder = ({ ctx, clipPrefix, inputs, clipDurationSec, clipStartSec }) => {
  const accent = accentFor(inputs);
  const headline = inputs.text ?? "";
  const eyebrow = inputs.subtext ?? "";
  const items = (inputs.items?.length ? inputs.items : ["SSL|lock", "Stripe|credit", "Garantie|badge", "Made in DE|flag", "Support|headphones"]).slice(0, 6);

  const lg = typeSize("lg", ctx.format);
  const sm = typeSize("sm", ctx.format);
  const xs = typeSize("xs", ctx.format);

  const wrapId = `${clipPrefix}-wrap`;
  const stripId = `${clipPrefix}-strip`;
  const pillIds = items.map((_, i) => `${clipPrefix}-pill-${i}`);

  const t = makeTl(clipStartSec);
  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "studio", accent });

  const header = sceneHeader({ clipPrefix, accent, eyebrow, title: headline, titlePx: lg * 0.78, subPx: sm * 0.95 });

  const pills = items.map((raw, i) => {
    const [label, iconKey] = splitItem(raw);
    const aliasKey = (iconKey ?? "shield").toLowerCase().replace(/[^a-z]/g, "");
    const iconName = ICON_ALIASES[aliasKey] ?? "shield";
    return `<div id="${pillIds[i]}" style="display:inline-flex;align-items:center;gap:12px;padding:16px 24px;border-radius:9999px;background:linear-gradient(165deg, ${withAlpha(themeColors.fg, 0.09)} 0%, ${withAlpha(themeColors.fg, 0.03)} 100%);border:1.5px solid ${withAlpha(accent, 0.4)};color:${themeColors.fg};font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${sm * 0.7}px;letter-spacing:1.5px;text-transform:uppercase;box-shadow:0 12px 30px rgba(0,0,0,0.35), inset 0 1px 0 ${withAlpha(themeColors.fg, 0.1)};backdrop-filter:blur(12px);-webkit-backdrop-filter:blur(12px);">
      <span style="color:${accent};display:inline-flex;">${lucideIcon(iconName, 22, accent, 2.2)}</span>
      <span>${escapeHtml(label ?? "")}</span>
    </div>`;
  }).join("");

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:36px;padding:72px;">
    ${header.html}
    <div id="${stripId}" style="display:flex;gap:12px;flex-wrap:wrap;align-items:center;justify-content:center;width:100%;max-width:1180px;">${pills}</div>
  </div>`;

  const init = [
    eyebrow ? gsapSetLine(`#${header.ids.eyebrow}`, { opacity: 0, y: 12 }) : "",
    headline ? gsapSetLine(`#${header.ids.title}`, { opacity: 0, y: 16 }) : "",
    ...pillIds.map((id) => gsapSetLine(`#${id}`, { opacity: 0, y: 20, scale: 0.9 })),
  ].filter(Boolean).join("\n");

  const tweens: string[] = [];
  if (eyebrow) tweens.push(t.to(`#${header.ids.eyebrow}`, { opacity: 1, y: 0, duration: 0.5, ease: "raw:\"power2.out\"" }, 0.0));
  if (headline) tweens.push(t.to(`#${header.ids.title}`, { opacity: 1, y: 0, duration: 0.6, ease: "raw:\"power2.out\"" }, 0.15));
  pillIds.forEach((id, i) => tweens.push(t.to(`#${id}`, { opacity: 1, y: 0, scale: 1, duration: 0.55, ease: "raw:\"back.out(1.6)\"" }, 0.4 + i * 0.1)));
  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
